import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: string;
              size?: string;
              shape?: string;
              text?: string;
              logo_alignment?: string;
              width?: number;
            }
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
// Google's button API takes a fixed pixel width, not a percentage — these
// bounds keep it full-width like the fields below it without overflowing
// the card's padding on narrow phones or stretching absurdly wide on tablets.
const MIN_BUTTON_WIDTH = 220;
const MAX_BUTTON_WIDTH = 400;

let gsiScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_GSI_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }

  return gsiScriptPromise;
}

interface GoogleSignInButtonProps {
  onCredential: (idToken: string) => void;
  /** True while another sign-in path (e.g. the password form) is in progress. */
  disabled?: boolean;
  /** True while this button's own credential is being exchanged with the backend. */
  loading?: boolean;
}

export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onCredential,
  disabled = false,
  loading = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  // Google's iframe button reads the callback once at initialize() time — a
  // ref lets the init/render effect run only once on mount instead of
  // re-running (and flashing the button) on every parent re-render.
  const onCredentialRef = useRef(onCredential);
  const lastWidthRef = useRef(0);
  const [scriptError, setScriptError] = useState(false);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  const renderButton = useCallback(() => {
    if (!buttonContainerRef.current || !wrapperRef.current || !window.google) return;

    const width = Math.min(
      MAX_BUTTON_WIDTH,
      Math.max(MIN_BUTTON_WIDTH, Math.round(wrapperRef.current.offsetWidth))
    );
    if (width === lastWidthRef.current) return;
    lastWidthRef.current = width;

    buttonContainerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(buttonContainerRef.current, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text: 'continue_with',
      logo_alignment: 'left',
      width,
    });
    setRendered(true);
  }, []);

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;

    loadGoogleIdentityServices()
      .then(() => {
        if (cancelled || !window.google) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => onCredentialRef.current(response.credential),
        });

        renderButton();
      })
      .catch(() => {
        if (!cancelled) setScriptError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [renderButton]);

  // Reflows the button on viewport/orientation changes (mobile is primary here).
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(renderButton, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeout) clearTimeout(timeout);
    };
  }, [renderButton]);

  if (scriptError || !import.meta.env.VITE_GOOGLE_CLIENT_ID) {
    return null;
  }

  const blocked = disabled || loading;
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <div
        ref={buttonContainerRef}
        style={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: 40,
          opacity: rendered ? 1 : 0,
          transition: reduceMotion ? 'none' : 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
      {/* Google's button is a cross-origin iframe — the only way to make it
          non-interactive while disabled/loading is to block clicks with an
          overlay, since the iframe itself can't be styled or disabled. */}
      {rendered && blocked && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            background: 'rgba(255, 255, 255, 0.72)',
            borderRadius: 6,
            cursor: loading ? 'default' : 'not-allowed',
          }}
        >
          {loading && (
            <>
              <Spin size="small" />
              <span style={{ fontSize: 14, color: '#595959' }}>Entrando com Google…</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
